<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Taxa de Conversao por Etapa',
  },
});

function getConversionColor(rate) {
  if (rate >= 50)
    return { bg: 'bg-n-teal-3', text: 'text-n-teal-11', bar: 'bg-n-teal-9' };
  if (rate >= 30)
    return { bg: 'bg-n-blue-3', text: 'text-n-blue-11', bar: 'bg-n-blue-9' };
  if (rate >= 15)
    return { bg: 'bg-n-amber-3', text: 'text-n-amber-11', bar: 'bg-n-amber-9' };
  return { bg: 'bg-n-ruby-3', text: 'text-n-ruby-11', bar: 'bg-n-ruby-9' };
}

function formatNumber(num) {
  if (!num) return '0';
  return num.toLocaleString('pt-BR');
}
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-git-merge text-lg text-n-amber-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div
      v-else-if="data.length === 0"
      class="flex items-center justify-center h-48 text-n-slate-11"
    >
      <span class="text-sm">Sem dados de conversao</span>
    </div>

    <div v-else class="flex flex-col gap-4">
      <div
        v-for="(funnel, funnelIdx) in data"
        :key="funnel.funnel_id"
        class="p-3 rounded-lg bg-n-alpha-1"
      >
        <div class="font-medium text-n-slate-12 mb-3">
          {{ funnel.funnel_name }}
        </div>

        <div class="flex flex-col gap-3">
          <div
            v-for="(conversion, idx) in funnel.conversions"
            :key="idx"
            class="relative"
          >
            <!-- Stage Names -->
            <div class="flex items-center justify-between mb-1">
              <div class="flex items-center gap-2 text-xs">
                <span
                  class="w-2 h-2 rounded"
                  :style="{
                    backgroundColor: conversion.from_color || '#94a3b8',
                  }"
                />
                <span class="text-n-slate-12">{{ conversion.from_stage }}</span>
                <span class="i-lucide-arrow-right text-n-slate-10" />
                <span
                  class="w-2 h-2 rounded"
                  :style="{ backgroundColor: conversion.to_color || '#94a3b8' }"
                />
                <span class="text-n-slate-12">{{ conversion.to_stage }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-xs text-n-slate-11">
                  {{ formatNumber(conversion.converted) }}/{{
                    formatNumber(conversion.from_count)
                  }}
                </span>
                <span
                  class="px-2 py-0.5 rounded text-xs font-bold"
                  :class="[
                    getConversionColor(conversion.rate).bg,
                    getConversionColor(conversion.rate).text,
                  ]"
                >
                  {{ conversion.rate?.toFixed(1) || 0 }}%
                </span>
              </div>
            </div>

            <!-- Progress Bar -->
            <div class="h-2 bg-n-alpha-2 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full transition-all duration-300"
                :class="getConversionColor(conversion.rate).bar"
                :style="{ width: `${conversion.rate || 0}%` }"
              />
            </div>
          </div>
        </div>

        <!-- Overall Conversion -->
        <div
          class="mt-4 pt-3 border-t border-n-weak flex items-center justify-between"
        >
          <span class="text-sm text-n-slate-11">Conversao Total (Primeira → Ultima)</span>
          <span
            class="px-3 py-1 rounded text-sm font-bold"
            :class="[
              getConversionColor(funnel.overall_conversion).bg,
              getConversionColor(funnel.overall_conversion).text,
            ]"
          >
            {{ funnel.overall_conversion?.toFixed(1) || 0 }}%
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
